import { useCallback, useState } from "react";

interface UseNativeInvoiceCloseGuardParams {
    // Whether the drawer session can be edited at all. A PDF-preview session
    // has nothing to lose, so it always closes straight away.
    isEditable: boolean;
    isDirty: boolean;
    onClose: () => void;
    // Saves the current form and resolves true only on success.
    onSave: () => Promise<boolean>;
}

interface NativeInvoiceCloseGuard {
    // Close request for the Drawer chrome and the command-bar Cancel.
    requestClose: () => void;
    isDiscardModalOpen: boolean;
    // Save-and-close from the discard modal; keeps the drawer open on failure.
    saveAndClose: () => Promise<void>;
    cancelClose: () => void;
}

// One close-confirmation guard shared by the Drawer chrome and the command bar
// Cancel. Prompts only for an editable, dirty session (§6.2). A successful Send
// bypasses this because it saves before closing via the raw onClose.
export function useNativeInvoiceCloseGuard({
    isEditable,
    isDirty,
    onClose,
    onSave,
}: UseNativeInvoiceCloseGuardParams): NativeInvoiceCloseGuard {
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
    const shouldPromptOnClose = isEditable && isDirty;

    const requestClose = useCallback(() => {
        if (shouldPromptOnClose) {
            setIsDiscardModalOpen(true);
            return;
        }
        onClose();
    }, [shouldPromptOnClose, onClose]);

    const saveAndClose = useCallback(async () => {
        const didSave = await onSave();
        if (didSave) {
            setIsDiscardModalOpen(false);
            onClose();
        }
    }, [onSave, onClose]);

    const cancelClose = useCallback(
        () => setIsDiscardModalOpen(false),
        [setIsDiscardModalOpen],
    );

    return { requestClose, isDiscardModalOpen, saveAndClose, cancelClose };
}
